import { defineDiagnosticQuiz } from "./_schema";
import type { Answers, Question } from "./_schema";
import { deriveFocusProfile } from "./quiz-focus-profile";

/**
 * IGNITE FOCUS QUIZ -- the cold-traffic quiz for the Ignite cognition product.
 *
 * The first `diagnostic`-kind quiz: it runs the persona flow's screens and
 * state machine but derives a `FocusProfile` instead of routing to one of the
 * six testosterone personas, none of which it has an honest value for.
 *
 * Copy is the operator's script (`ILP - Quiz.docx`, "Ignite Quiz Copy
 * (General) v3") verbatim, with the four agreed fixes:
 *   - "Spoiler Alert:" removed from the first interstitial (register);
 *   - "I don't drink coffee" added to the coffee screen (an escape);
 *   - "A prescription stimulant" added to the tried list, because the
 *     Adderall quitter is a week-one priority buyer and was otherwise
 *     unroutable;
 *   - the script's duplicated Q3 resolved by numbering screens, not questions.
 *
 * One screen is NOT in the script: `cost` ("What has this already cost you?"),
 * the tolerance check from the flow design that the deriver reads for
 * quality-of-life impact. Kept alongside the script's own closing aspiration
 * screen (`want-most`) rather than in place of it, so no operator copy was
 * dropped and every screen the deriver reads exists. Cutting either is one
 * array entry; `deriveQol` degrades to the last-week count without `cost`.
 *
 * Every string is written inside the claims boundary in
 * docs/ignite-cognition-brief.md, section 2. Option values are the DERIVER
 * CONTRACT -- `quiz-focus-profile.ts` and `quiz-focus-day.ts` read them by
 * name, and tests/quizzes/quiz-focus.test.ts pins the set.
 *
 * No testimonial on screen one: every review on file predates launch and the
 * verified badge is unresolved (brief, section 7). No email gate: identity
 * arrives via the order-time back-fill.
 *
 * Flow and verdict specs: docs/specs/2026-09-09-ignite-focus-quiz-design.md,
 * docs/specs/2026-09-09-ignite-focus-result-page.md.
 */

const QUESTION_ORDER = [
  "age",
  "stakes",
  "last-week",
  "info-not-discipline",
  "task-switch",
  "phone-reach",
  "reread",
  "afternoon-wall",
  "coffee-effect",
  "room",
  "waking",
  "info-focus-loop",
  "how-long",
  "already-tried",
  "cost",
  "if-nothing-changes",
  "want-most",
  "analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    // Bands run younger than the testosterone quizzes because this buyer does.
    // The deriver gates age-related decline on the two upper bands.
    options: [
      { value: "under-35", label: "Under 35" },
      { value: "35-44", label: "35 to 44" },
      { value: "45-54", label: "45 to 54" },
      { value: "55-plus", label: "55 or older" },
    ],
  },
  {
    id: "stakes",
    type: "single",
    prompt: "The fog. The lack of focus. The lost time… Where is it hitting you hardest?",
    options: [
      { value: "work", label: "My work. I'm not feeling as sharp as I used to" },
      { value: "family", label: "My family. I'm in the room but not really present" },
      { value: "goals", label: "My goals. I procrastinate starting the things I want to accomplish" },
      { value: "energy", label: "My energy. I can barely make it through the day" },
    ],
  },
  {
    id: "last-week",
    type: "multi",
    prompt: "Which of these happened in the last week? Be honest.",
    helper: "Check all that apply.",
    min: 1,
    // He counts his own evidence here; the count is the heaviest severity
    // input and "None of these" is the only way onto the all-clear branch.
    options: [
      { value: "didnt-hear", label: "Someone was talking, and I didn't hear a word" },
      { value: "fumbled", label: "I fumbled something I'd have crushed five years ago" },
      { value: "nothing-left", label: "I got home with nothing left for anyone" },
      { value: "snapped", label: "I snapped over something small" },
      { value: "none", label: "None of these", pinned: true },
    ],
    exclusiveValues: ["none"],
  },
  {
    id: "info-not-discipline",
    type: "info",
    // Interstitial one: absolution at the low point, right after he counted.
    // Names no mechanism -- that is interstitial two's job. The script's
    // closing line is split into a body line and the button.
    heading: "You're not losing your edge.",
    body: [
      "Your attention is getting taken from you, one interruption at a time. It isn't a lack of discipline.",
      "You're one minute away from clarity.",
    ],
    cta: "Keep going",
    // Below the CTA, per rule 20 -- this screen's CTA is in flow, so the order
    // is body, button, picture, and the picture is support rather than the
    // argument. It shows attention leaving rather than a mechanism: naming the
    // mechanism is the NEXT screen's job, and the loop diagram that does it
    // deliberately lives there instead of here.
    image: {
      src: "/static/images/ignite-brain-choline.webp",
      alt: "A man's head in profile, his brain lit yellow, trailing away into scattered wisps and molecules.",
    },
  },
  {
    id: "task-switch",
    type: "single",
    prompt:
      "How often does this happen: You sit down to do one thing, and twenty minutes later you've done five other things instead.",
    options: [
      { value: "most-days", label: "Most days" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "once-in-a-while", label: "Once in a while" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "phone-reach",
    type: "single",
    prompt: "Do you reach for your phone even though you have other things to do?",
    options: [
      { value: "constantly", label: "Yes, constantly" },
      { value: "more-than-before", label: "Yes, a lot more than I used to" },
      { value: "sometimes", label: "Sometimes" },
      { value: "not-really", label: "Not really" },
    ],
  },
  {
    id: "reread",
    type: "single",
    prompt:
      "Do you reread the same email, text, or paragraph multiple times before it actually lands?",
    options: [
      { value: "most-days", label: "Yes, most days" },
      { value: "when-tired", label: "Yes, when I'm tired" },
      { value: "occasionally", label: "Occasionally" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "afternoon-wall",
    type: "single",
    prompt: "Do you hit a wall in the afternoon that coffee doesn't fix?",
    options: [
      { value: "almost-daily", label: "Yes, almost every day around 2 or 3pm" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "after-bad-sleep", label: "Only after a bad night's sleep" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "coffee-effect",
    type: "single",
    prompt: "What does your coffee actually do for you?",
    options: [
      { value: "wires-then-drops", label: "Wires me up, then drops me" },
      { value: "takes-edge-off", label: "Takes the edge off, doesn't help me think clearly" },
      { value: "need-more", label: "Still works, but I need more of it than I used to" },
      { value: "looking-for-something", label: "It works, but I am looking for something else" },
      { value: "no-coffee", label: "I don't drink coffee" },
    ],
  },
  {
    id: "room",
    type: "single",
    prompt: "How often do you walk into a room and forget why?",
    options: [
      { value: "daily", label: "Daily" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "now-and-then", label: "Now and then" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "waking",
    type: "single",
    prompt: "How do you feel when you wake up?",
    options: [
      { value: "tired-anyway", label: "I sleep okay but wake up just as tired" },
      { value: "slow-start", label: "Slow. Takes an hour and a caffeine hit" },
      { value: "waking-at-2", label: "Waking up at 2 or 3 am, then groggy in the morning" },
      { value: "rested", label: "Rested" },
    ],
  },
  {
    id: "info-focus-loop",
    type: "info",
    // Interstitial two: the mechanism reveal. The CTA pins so it is reachable
    // at every scroll position of a reading screen (rule 20's goal).
    heading: "Your answers point to a loop, not a lack of willpower.",
    body: [
      "Every interruption costs you a little of the chemistry your brain uses to lock back in. Chase it with more caffeine and you spend even more of it. Then the crash sends you looking for another hit.",
      "That's the Focus Loop. Most men run it all day without ever noticing. And it doesn't stay at work. It follows you home…",
    ],
    // The loop itself, drawn. It belongs on THIS screen and not the earlier
    // bridge: the picture spells the cycle out in words (sit down,
    // procrastinate, focus, scroll) and the earlier screen is the absolution
    // beat, which names no mechanism on purpose. Showing the loop there spends
    // this reveal a screen early and illustrates a claim that screen never
    // makes.
    //
    // The CTA is pinned (rule 20, since 2026-09-11 that is every info screen
    // and needs no flag), so it leaves the flow: the render order here is body
    // then image, with the button held over both.
    image: {
      src: "/static/images/ignite-focus-loop.webp",
      alt: "A circle with no end: sit down, procrastinate, focus, scroll, procrastinate, focus, and back to sitting down.",
    },
  },
  {
    id: "how-long",
    type: "single",
    prompt: "How long have you been noticing this?",
    options: [
      { value: "lt-6-months", label: "Less than 6 months" },
      { value: "6-12-months", label: "6 months to a year" },
      { value: "over-a-year", label: "Over a year" },
      { value: "years", label: "Years now" },
    ],
  },
  {
    id: "already-tried",
    type: "multi",
    prompt: "What have you already tried?",
    helper: "Check all that apply.",
    min: 1,
    // "A prescription stimulant" is the one option that identifies the
    // Adderall quitter. Result and offer copy must never call Ignite an
    // alternative to it or imply replacing medication (brief, section 2).
    options: [
      { value: "more-coffee", label: "More coffee" },
      { value: "energy-drinks", label: "Energy drinks or pre-workout" },
      { value: "nootropic", label: "Another nootropic or focus supplement" },
      { value: "prescription-stimulant", label: "A prescription stimulant" },
      { value: "nothing-yet", label: "Nothing yet", pinned: true },
    ],
    exclusiveValues: ["nothing-yet"],
  },
  {
    id: "cost",
    type: "single",
    // The tolerance check. Every option but the first is him saying out loud
    // that the problem has a price; the deriver reads it for QoL impact.
    prompt: "What has this already cost you?",
    options: [
      { value: "nothing-yet", label: "Nothing serious yet" },
      { value: "work-slipping", label: "Work slipping to tomorrow, over and over" },
      { value: "people-noticed", label: "People at work have noticed" },
      { value: "outpaced", label: "I'm worried about being outpaced" },
      { value: "evenings-gone", label: "The evenings. There's nothing left of me by six." },
    ],
  },
  {
    id: "if-nothing-changes",
    type: "single",
    prompt: "If nothing changes, where will you be 90 days from now?",
    options: [
      { value: "further-behind", label: "Further behind at work than I want to admit" },
      { value: "family-suffers", label: "My family will struggle because I'm not present" },
      { value: "never-finish", label: "Still starting things I never finish" },
      { value: "just-getting-by", label: "Just getting by, and getting used to it" },
      { value: "dont-want-to-know", label: "I don't want to find out" },
    ],
  },
  {
    id: "want-most",
    type: "single",
    // The one aspiration screen, placed last so the analyzing beat opens on
    // what he wants rather than what it costs. Not read by the deriver.
    prompt: "When you picture getting your focus back, what do you want most?",
    options: [
      { value: "focused-hours", label: "A few focused hours where things actually get done" },
      { value: "sharp-as-before", label: "To feel as sharp as I used to be" },
      { value: "present", label: "To be present and focused on the things that matter most" },
      { value: "all-of-it", label: "All of it. I want to feel like myself again" },
    ],
  },
  {
    id: "analyzing",
    type: "info",
    // Peak-suspense beat. "Four" is the four causes in `FOCUS_CAUSES`.
    heading: "Analyzing your focus profile...",
    body: ["Matching your answers against the four most common attention drains in men."],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizFocusQuiz = defineDiagnosticQuiz({
  id: "quiz-focus",
  slug: "quiz-focus",
  kind: "diagnostic",
  /**
   * Ignite is a different product from Boost, and it is yellow, not house
   * orange. This one field re-points every accent the quiz renders -- the
   * highlighted word in the intro headline, the answer pill it fills on tap,
   * the progress bar, the focus ring, the analyzing ring, the cause icons and
   * every CTA -- via four CSS variables in `web/src/brand/tokens.css`. Four,
   * because the cause icons and labels sit on LIGHT cards where the accent
   * itself is invisible, and the pills carry text ON the accent where white
   * is: contrast travels with the colour rather than beside it.
   *
   * It is not a CTA-only exception any more. The CTA was yellow and everything
   * around it was orange, which read as two products on one screen.
   */
  theme: "ignite",
  meta: {
    title: "What's Really Stealing Your Focus?",
    introHeadline: "What's Really Stealing Your Focus?",
    introAccent: "Really",
    introBody:
      "Take the 2 min quiz to discover what's affecting your focus and the 45 second routine designed to amplify how you operate.",
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    return QUESTION_ORDER[idx + 1] ?? null;
  },

  deriveFocusProfile,
});
